<template>
    <div class="not-invoiced-order-form-page-1">
        <div class="form-group row">
            <label class="col-md-2 col-form-label" for="subcustomerName">Nom du client (Nom et prénom)</label>
            <div class="col-md-4">
                <template v-if="!order">
                    <v-select
                        v-dusk="'customer-search'"
                        label="name"
                        :options="subcustomersSearchOptions"
                        :filterable="false"
                        :input-id="'subcustomerSearch'"
                        @search="onSubcustomerSearch"
                        @input="onSubcustomerSelect"
                    >
                        <template slot="no-options">
                            Indiquez le nom du client (3 caractères minimum)
                        </template>
                        <template slot="option" slot-scope="option">
                            <div class="d-center" v-dusk="'customer-search-' + (option.index || 'new-entry')">
                                <div>
                                    {{ option.name }}
                                </div>
                                <div v-if="option.comment">
                                    <i>{{ option.comment }}</i>
                                </div>
                                <div v-if="option.phone">
                                    <i class="fad fa-phone-square"></i> {{ option.phone }}
                                </div>
                                <div v-if="option.email">
                                    <i class="fad fa-envelope-open-text"></i> {{ option.email }}
                                </div>
                            </div>
                        </template>
                    </v-select>
                    <input class="form-control" id="subcustomerName" type="hidden" v-model="lSubcustomerName">
                </template>
                <template v-else>
                    <input class="form-control" id="subcustomerName" type="text" v-model="lSubcustomerName">
                </template>
            </div>
        </div>

        <div class="form-group row">
            <label class="col-md-2 col-form-label" for="subcustomerPhone">Numéro de téléphone</label>
            <div class="col-md-4">
                <input class="form-control" id="subcustomerPhone" type="text" v-model="lSubcustomerPhone">
            </div>
        </div>

        <div class="form-group row">
            <label class="col-md-2 col-form-label" for="subcustomerEmail">Adresse email</label>
            <div class="col-md-4">
                <input class="form-control" id="subcustomerEmail" type="email" v-model="lSubcustomerEmail">
            </div>
        </div>


        <template v-if="!userFilled && lSubcustomerName">
            <div class="form-group row">
                <div class="col-md-4 offset-md-2">
                    <button type="button" class="btn btn-sm btn-outline-danger" @click="skipSubcustomerContact = true">
                        <i class="fas fa-chevron-right"></i> Ne pas indiquer de moyen de contacter le client
                    </button>
                </div>
            </div>
        </template>

        <ErrorsZone :errors="errorsUserContact" />

        <template v-if="userFilled">
            <div class="form-group row">
                <label class="col-md-2 col-form-label" for="customer">Lieu de retrait</label>
                <div class="col-md-4">
                    <v-select
                        v-dusk="'pickup-point-select'"
                        id="customer"
                        v-model="lSelectedCustomer"
                        :options="customers"
                        :input-id="'pickupPointSearch'"
                        label="name">
                        <div slot="no-options">Aucun lieu de retrait ne correspond à votre recherche</div>
                    </v-select>
                </div>
            </div>

            <div v-if="lSelectedCustomer" class="form-group row">
                <div class="col-md-4 offset-md-2">
                    <b>Jours de livraison :</b> {{ this.lSelectedCustomer.delivery_days.join(', ') }}<br>
                    <b>Jour de fin de préparation :</b> {{ this.lSelectedCustomer.end_preparation_day }}<br>
                </div>
            </div>

            <div v-if="lSelectedCustomer" class="form-group row">
                <label class="col-md-2 col-form-label" for="deliveryDate">Date de livraison</label>
                <DeliveryDatepicker
                    ref="deliveryDatepicker"
                    id="deliveryDate"
                    class="delivery-date col-md-10"
                    :customer="customer"
                    :initial-date="lDeliveryDate && lDeliveryDate.date"
                    :order="order"
                    :can-select-red-dates="canSelectRedDates"
                    v-model="lDeliveryDate"
                />
            </div>

            <template v-if="lDeliveryDate">
                <div class="form-group row">
                    <label class="col-md-2 col-form-label" for="endPreparationDate">Date de fin de préparation</label>
                    <ForcePreparationDatepicker
                        ref="endPreparationDatepicker"
                        id="endPreparationDate"
                        class="col-md-10"
                        :customer="customer"
                        :delivery-date="lDeliveryDate"
                        :can-select-red-dates="canSelectRedDates"
                        v-model="lEndPreparationDate"
                    />
                </div>
                <div class="form-group row">
                    <div v-if="warningZoneDisplayed" class="offset-md-2 alert alert-warning">
                        <span class="alert-heading h4">
                            <i class="fad fa-exclamation-triangle"></i> Attention : validation requise
                        </span>
                        <ul class="mt-2">
                            <li v-for="message in warningMessages">{{ message }}</li>
                        </ul>
                    </div>
                </div>
                <div class="form-group row">
                    <div v-if="dangerZoneDisplayed" class="offset-md-2 alert alert-danger">
                        <span class="alert-heading h4">
                            <i class="fad fa-exclamation-triangle"></i> Attention : validation requise
                        </span>
                        <ul class="mt-2">
                            <li v-for="message in dangerMessages">{{ message }}</li>
                        </ul>
                    </div>
                </div>
            </template>
        </template>

        <hr>

        <div class="row">
            <div class="col-md-12">
                <a v-if="backUrl" class="btn btn-outline-dark float-left" :href="backUrl">
                    <i class="fas fa-chevron-left"></i> Quitter la prise de commande
                </a>

                <template v-if="(userFilled || userFilledOnce) && lEndPreparationDate">
                    <template v-if="needAuthorizationToken && !authorizationToken">
                        <AuthorizationModal
                            token-type="PickupPointSubcustomerOrder"
                            :token-payload="authorizationTokenPayload"
                            @authorised="onAuthorizationModalAuthorize"
                        >
                            <template #button="sp">
                                <AuthorizationModalButton
                                    :class="dangerZoneDisplayed ? 'btn-danger' : 'btn-warning'"
                                    :disabled="!canClickNext"
                                    @click="sp.showModal()"
                                >
                                    Suivant
                                </AuthorizationModalButton>
                            </template>
                            <template #messages>
                                <AlertsZone
                                    v-if="warningZoneDisplayed"
                                    type="warning"
                                    title="Attention : validation requise"
                                    :messages="warningMessages"
                                />
                                <AlertsZone
                                    v-if="dangerZoneDisplayed"
                                    type="danger"
                                    title="Attention : validation requise"
                                    :messages="dangerMessages"
                                />
                            </template>
                        </AuthorizationModal>
                    </template>
                    <button v-else type="button" class="btn btn-success float-right" @click="onClickNext" :disabled="!canClickNext">
                        <i class="fas fa-chevron-right"></i> Suivant
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import vSelect from 'vue-select';

    import {library} from '@fortawesome/fontawesome-svg-core'
    import {FontAwesomeIcon, FontAwesomeLayers} from "@fortawesome/vue-fontawesome";
    import {momentDay, momentDaysAreSame} from "../../moment-with-helpers";

    import {faAddressCard, faLockAlt} from '@fortawesome/pro-duotone-svg-icons';
    import {faCrown} from '@fortawesome/pro-solid-svg-icons'

    import AlertsZone from "../AlertsZone"
    import AuthorizationModal from "../AuthorizationModal";
    import AuthorizationModalButton from "../AuthorizationModalButton"
    import DeliveryDatepicker from "../DeliveryDatepicker";
    import ForcePreparationDatepicker from "../ForcePreparationDatepicker";
    import ErrorsZone from "../ErrorsZone";
    import {usePostRequest} from "../compositions/axios/postRequest";
    import {
        dangerZoneDisplayed,
        orderDatesDangerMessages,
        orderDatesWarningMessages,
        warningZoneDisplayed
    } from "../compositions/OrderWarnings"

    export default {
        components: {
            'v-select': vSelect,
            'font-awesome-icon': FontAwesomeIcon,
            'font-awesome-layers': FontAwesomeLayers,
            AlertsZone,
            AuthorizationModal,
            AuthorizationModalButton,
            DeliveryDatepicker,
            ForcePreparationDatepicker,
            ErrorsZone,
        },

        setup() {
            const {
                loading: sending,
                errors,
                post
            } = usePostRequest();

            return {
                sending,
                errors,
                post
            };
        },

        props: {
            order: {
                type: Object,
                required: false,
            },
            customers: {
                type: Array,
                required: true,
            },
            subcustomer: {
                type: Object,
                required: false,
            },
            selectedCustomer: {
                type: Object,
                required: false,
            },
            deliveryDate: {
                type: Object,
                required: false,
            },
            endPreparationDate: {
                type: Object,
                required: false,
            },
            canSelectRedDates: {
                type: Boolean,
                required: true,
            },
            backUrl: {
                type: String,
                required: false,
            },
        },

        data: function () {
            return {
                lSubcustomerName: this.subcustomer.name,
                lSubcustomerPhone: this.subcustomer.meta.phone,
                lSubcustomerEmail: this.subcustomer.meta.email,

                lSelectedCustomer: this.selectedCustomer,
                lDeliveryDate: this.deliveryDate,
                lEndPreparationDate: this.endPreparationDate,

                authorizationToken: null,

                errorsUserContact: null,

                userFilledOnce: false,

                skipSubcustomerContact: false,

                subcustomersSearchOptions: [],
            };
        },

        created() {
            library.add(faAddressCard, faCrown, faLockAlt);
        },

        methods: {
            onAuthorizationModalAuthorize(token) {
                this.authorizationToken = token;

                this.onClickNext();
            },

            trimOnChange(e) {
                e.target.value = _.trim(e.target.value);
            },

            validateFields() {
                this.errorsUserContact = null;

                let errors = {};

                this.lSubcustomerName = _.trim(this.lSubcustomerName);
                this.lSubcustomerPhone = _.trim(this.lSubcustomerPhone);
                this.lSubcustomerEmail = _.trim(this.lSubcustomerEmail);

                if (this.lSubcustomerName === '') {
                    errors['name'] = ["Le nom du client est requis."];
                }
                if (!(this.lSubcustomerPhone === '') && !(/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/.test(this.lSubcustomerPhone))) {
                    errors['phone'] = ["Le numéro de téléphone est invalide."];
                }
                if (!(this.lSubcustomerEmail === '') && !(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(this.lSubcustomerEmail))) {
                    errors['email'] = ["L'adresse email est invalide."];
                }

                if (Object.keys(errors).length > 0) {
                    this.errorsUserContact = { message: "Merci de corriger les erreurs suivantes :", errors: errors };
                    return false;
                }

                return true;
            },

            onClickNext() {
                if (this.validateFields()) {
                    this.$emit('submit', {
                        subcustomer: {
                            name: this.lSubcustomerName,
                            meta: {
                                phone: this.lSubcustomerPhone,
                                email: this.lSubcustomerEmail,
                            }
                        },
                        selectedCustomer: this.lSelectedCustomer,
                        deliveryDate: this.lDeliveryDate,
                        endPreparationDate: this.lEndPreparationDate,
                        authorizationToken: this.authorizationToken,
                    });
                }
            },

            onSubcustomerSearch(search, loading) {
                loading(true);
                this.subcustomerSearch(loading, search);
            },

            onSubcustomerSelect(value) {
                if(value) {
                    this.lSubcustomerName = value.name;
                    if(value.phone) {
                        this.lSubcustomerPhone = value.phone;
                    }
                    if(value.email) {
                        this.lSubcustomerEmail = value.email;
                    }
                }
            },

            async subcustomerSearch(loading, search) {
                if(search && search.length >= 3) {
                    const data = await this.post(`/espace-employes/commandes/client-point-retrait/recherche/json`, {
                        search: search
                    });
                    if (data) {
                        this.subcustomersSearchOptions = data.subcustomers.map(function(item, index) {
                            return {
                                index,
                                ...item,
                            }
                        });
                        this.subcustomersSearchOptions.unshift({
                            'name': search,
                            'comment': 'Créer un nouveau client',
                        });
                    }
                } else {
                    this.subcustomersSearchOptions = [];
                }
                loading(false);
            },
        },

        watch: {
            lSelectedCustomer(newValue, oldValue) {
                this.lDeliveryDate = null;
                this.lEndPreparationDate = null;
            },

            lDeliveryDate(newValue, oldValue) {
                if (!newValue || !oldValue || !momentDaysAreSame(newValue.date, oldValue.date)) {
                    this.authorizationToken = null;
                    this.lEndPreparationDate = null;
                }
            },

            lEndPreparationDate(newValue, oldValue) {
                if (!newValue || !oldValue || !momentDaysAreSame(newValue.date, oldValue.date)) {
                    this.authorizationToken = null;
                }
            },
        },

        computed: {
            customer() {
                if (!!this.lSelectedCustomer) {
                    return {type: 'pickup_point', id: this.lSelectedCustomer.id};
                }
            },

            userFilled() {
                const res = !!this.lSubcustomerName && (!!this.lSubcustomerPhone || !!this.lSubcustomerEmail || this.skipSubcustomerContact);
                this.userFilledOnce = this.userFilledOnce || res;
                return res;
            },

            canClickNext() {
                return this.userFilled && !!this.lSelectedCustomer && !!this.lDeliveryDate && !!this.lEndPreparationDate;
            },

            endPreparationDatePassed() {
                return this.lEndPreparationDate && momentDay(this.lEndPreparationDate.date).isBefore(momentDay());
            },

            formattedLEndPreparationDateYmd() {
                return momentDay(this.lEndPreparationDate.date).format('YYYY-MM-DD');
            },

            deliveryDatePassed() {
                return this.lDeliveryDate && momentDay(this.lDeliveryDate.date).isBefore(momentDay());
            },

            deliveryDateIsNotUsual() {
                if (this.lDeliveryDate) {
                    return this.lDeliveryDate.permission === 'restricted';
                }
                return false;
            },

            deliveryDateIsForbidden() {
                if (this.lDeliveryDate) {
                    return this.lDeliveryDate.permission === 'forbidden';
                }
                return false;
            },

            warningMessages() {
                return orderDatesWarningMessages(this.lDeliveryDate)
            },

            dangerMessages() {
                return orderDatesDangerMessages(this.lDeliveryDate, this.lEndPreparationDate)
            },

            warningZoneDisplayed,

            dangerZoneDisplayed,

            needAuthorizationToken() {
                const warningsDisplayed = this.dangerZoneDisplayed || this.warningZoneDisplayed

                let importantStateChanged = true
                if (this.order) {
                    importantStateChanged =
                        !momentDaysAreSame(this.deliveryDate.date, this.lDeliveryDate.date)
                        || !momentDaysAreSame(this.endPreparationDate.date, this.lEndPreparationDate.date)
                        || this.selectedCustomer.id !== this.lSelectedCustomer.id
                }

                return warningsDisplayed && importantStateChanged && !this.authorizationToken
            },

            authorizationTokenPayload() {
                return {
                    order_id: (this.order && this.order.id) || null,
                    token_type: 'PickupPointSubcustomerOrder',
                    subcustomer_id: (this.order && this.order.fk_subcustomer) || null,
                    subcustomer_name: this.lSubcustomerName,
                    subcustomer_phone: this.lSubcustomerPhone,
                    subcustomer_email: this.lSubcustomerEmail,
                    customer_id: this.lSelectedCustomer.id,
                    delivery_date: momentDay(this.lDeliveryDate.date).format('YYYY-MM-DD'),
                    end_preparation_date: momentDay(this.lEndPreparationDate.date).format('YYYY-MM-DD'),
                    paid_at: null, // avoid validating edit_order_paid special permission
                };
            },
        }
    }
</script>

<style lang="scss">
    .not-invoiced-order-form-page-1 {
        .btn-social > :first-child {
            height: inherit;
        }
    }
</style>
